import { Box, Typography } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation } from 'react-router-dom';
import MarkdownRenderer from '../../../../components/MarkdownRenderer';

const MessageBox = ({
    messageText,
    timestamp,
    isRight,
    status,
    id,
    files,
    dateIndex
}) => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tab = queryParams.get('tab') || 'all';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: isRight ? 'end' : 'start',
                gap: '2px',
                py: '3px',
                px: '8px',
                width: '100%'
            }}
        >
            <Box
                className={isRight ? 'bg-slate-200' : 'bg-sky-200'}
                sx={{
                    minWidth: 'min-content',
                    maxWidth: '580px',
                    px: '16px',
                    pt: '8px',
                    pb: '2px',
                    borderRadius: isRight
                        ? '8px 0px 8px 8px'
                        : '0px 8px 8px 8px'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end'
                    }}
                >
                    <Typography variant="body2">
                        <MarkdownRenderer>
                            {messageText ? messageText : ''}
                        </MarkdownRenderer>
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                files && files?.length > 1
                                    ? 'repeat(2, 1fr)'
                                    : '1fr',
                            gap: '10px'
                        }}
                    >
                        {files &&
                            files?.map((file, index) => {
                                if (
                                    file?.file_type === 'image/jpeg' ||
                                    file?.file_type === 'image/png'
                                ) {
                                    return (
                                        <Box key={index}>
                                            <img
                                                src={`/chat-media/${file?.file_url}`}
                                                alt="file"
                                                style={{
                                                    width: '150px',
                                                    height: '150px',
                                                    borderRadius: '8px',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        </Box>
                                    );
                                } else {
                                    // Show file type and download link for other files
                                    let fileTypeLabel = '';
                                    if (file?.file_type === 'application/pdf') {
                                        fileTypeLabel = 'PDF';
                                    } else if (
                                        file?.file_type ===
                                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                    ) {
                                        fileTypeLabel = 'DOCX';
                                    } else if (
                                        file?.file_type ===
                                        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                                    ) {
                                        fileTypeLabel = 'XLSX';
                                    } else if (
                                        file?.file_type ===
                                        'application/msword'
                                    ) {
                                        fileTypeLabel = 'DOC';
                                    } else if (
                                        file?.file_type ===
                                        'application/vnd.ms-excel'
                                    ) {
                                        fileTypeLabel = 'XLS';
                                    } else if (
                                        file?.file_type === 'text/plain'
                                    ) {
                                        fileTypeLabel = 'TXT';
                                    } else {
                                        fileTypeLabel =
                                            file?.file_type.split('/')[1]?.toUpperCase() ||
                                            file?.file_type;
                                    }
                                    return (
                                        <Box
                                            key={index}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                background: '#f3f4f6',
                                                borderRadius: '6px',
                                                px: 1,
                                                py: 0.5,
                                                maxWidth: '200px'
                                            }}
                                        >
                                            <a
                                                href={`/chat-media/${file?.file_url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    textDecoration: 'none',
                                                    color: 'inherit',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: '0.9rem',
                                                        color: '#2563eb'
                                                    }}
                                                >
                                                    {fileTypeLabel}
                                                </Box>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.85rem',
                                                        color: 'text.primary',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        maxWidth: '120px'
                                                    }}
                                                    title={file?.file_name}
                                                >
                                                    {file?.file_name}
                                                </Typography>
                                            </a>
                                        </Box>
                                    );
                                }
                            })}
                    </Box>
                </Box>

                <Box
                    sx={{
                        textAlign: 'end',
                        display: 'flex',
                        gap: '4px',
                        justifyContent: 'end',
                        alignItems: 'end'
                    }}
                >
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            fontSize: '0.73rem'
                        }}
                    >
                        {formatTime(timestamp)}
                    </Typography>
                    {isRight && tab === 'all' && (
                        <Typography
                            sx={{
                                color: 'text.secondary'
                            }}
                        >
                            {status === 'sent' ? (
                                <DoneIcon fontSize="0.7rem" />
                            ) : status === 'delivered' ? (
                                <DoneAllIcon fontSize="0.7rem" />
                            ) : status === 'read' ? (
                                <DoneAllIcon
                                    className="text-green-600"
                                    fontSize="0.7rem"
                                />
                            ) : (
                                <AccessTimeIcon fontSize="0.6rem" />
                            )}
                        </Typography>
                    )}
                    {isRight && tab === 'groups' && (
                        <Typography
                            sx={{
                                color: 'text.secondary'
                            }}
                        >
                            {status === 'sent' ? (
                                <DoneIcon fontSize="0.7rem" />
                            ) : status === 'delivered' ? (
                                <DoneAllIcon fontSize="0.7rem" />
                            ) : status === 'read' ? (
                                <DoneAllIcon fontSize="0.7rem" />
                            ) : null}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// Function to format date to HH:MM AM/PM in user timezone
const formatTime = (value) => {
    // if it's a pure UNIX-seconds string, multiply; otherwise assume ISO
    const date = /^\d+$/.test(value)
        ? new Date(Number(value) * 1000)
        : new Date(value);
    return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: 'numeric'
    });
};

export default MessageBox;
